import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Pagination,
  Paper,
  Rating,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  StarRate as StarRateIcon,
  Download as DownloadIcon,
  CallSplit as CallSplitIcon,
  Flag as FlagIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import api, { communityAPI } from '../services/api';
import { getCurrentUser } from '../services/auth';

const buildAuthorName = (author) => {
  if (!author) {
    return 'Unknown author';
  }
  const first = author.firstName || '';
  const last = author.lastName || '';
  const full = `${first} ${last}`.trim();
  return full || author.email || 'Unknown author';
};

const formatRelativeDate = (value) => {
  if (!value) {
    return 'Unknown';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }
  return formatDistanceToNow(parsed, { addSuffix: true });
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Unknown';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }
  return format(parsed, 'MMM dd, yyyy HH:mm');
};

const buildBadges = (caseItem) => {
  const badges = [];
  const stats = caseItem?.community?.stats || {};
  const moderation = caseItem?.community?.moderation || {};

  if ((stats.popularityScore || 0) >= 3) {
    badges.push('Popular');
  }
  if ((stats.completionRate || 0) >= 0.7) {
    badges.push('High completion rate');
  }
  const approvedRole = (caseItem?.community?.approvedByRole || '').toLowerCase();
  if (approvedRole.includes('admin') || approvedRole.includes('instructor')) {
    badges.push('Faculty reviewed');
  }
  const updatedAt = caseItem?.updatedAt || caseItem?.community?.publishedAt || caseItem?.createdAt;
  if (updatedAt) {
    const updatedDate = new Date(updatedAt);
    const diffDays = (Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays <= 7) {
      badges.push('Updated recently');
    }
  }
  if (moderation.flagged) {
    badges.push('Reported');
  }

  return badges;
};

const COMMENT_MAX_LENGTH = 5000;
const DEFAULT_VISIBLE_REPLIES = 3;

const resolveCommunityOriginId = (caseItem) => {
  const provenance = caseItem?.provenance || {};
  const originId = provenance.originCaseId || provenance.sourceCaseId || caseItem?.originCaseId || caseItem?.sourceCaseId;
  return originId ? originId.toString() : '';
};

const caseHasExaminerQuestions = (caseItem) => (
  Array.isArray(caseItem?.examinerQuestions) && caseItem.examinerQuestions.length > 0
);

const caseHasLearningMaterials = (caseItem) => {
  const learning = caseItem?.learning || {};
  return Boolean(
    (learning.summary && learning.summary.trim())
    || (Array.isArray(learning.links) && learning.links.length)
    || (Array.isArray(learning.attachments) && learning.attachments.length)
  );
};

const CommunityCasePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentPage, setCommentPage] = useState(1);
  const [commentPagination, setCommentPagination] = useState({ pages: 1, total: 0 });
  const [replyDraft, setReplyDraft] = useState({ parentId: null, body: '' });
  const currentUser = useMemo(() => getCurrentUser(), []);
  const [ratings, setRatings] = useState({
    realism: 0,
    difficulty: 0,
    markSchemeQuality: 0,
    examinerQuestions: null,
    learningMaterials: null
  });
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [actionState, setActionState] = useState({ action: null });
  const [reportDialog, setReportDialog] = useState({ open: false, reason: '', details: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [importSummary, setImportSummary] = useState(null);
  const [importSummaryLoading, setImportSummaryLoading] = useState(false);
  const [replyVisibility, setReplyVisibility] = useState({});

  const stats = caseData?.community?.stats || {};
  const hasExaminerQuestions = useMemo(() => caseHasExaminerQuestions(caseData), [caseData]);
  const hasLearningMaterials = useMemo(() => caseHasLearningMaterials(caseData), [caseData]);
  const isOriginalAuthor = useMemo(() => {
    const authorId = caseData?.author?._id || caseData?.author?.id || caseData?.author;
    const userId = currentUser?._id || currentUser?.id;
    if (!authorId || !userId) {
      return false;
    }
    return authorId.toString() === userId.toString();
  }, [caseData, currentUser]);
  const badges = useMemo(() => buildBadges(caseData), [caseData]);
  const threadedComments = useMemo(() => {
    const byId = new Map();
    const roots = [];
    (comments || []).forEach((comment) => {
      const id = comment?._id?.toString?.() || '';
      if (!id) {
        return;
      }
      byId.set(id, { ...comment, replies: [] });
    });

    byId.forEach((comment) => {
      const parentId = comment.parentId?.toString?.() || '';
      if (parentId && byId.has(parentId)) {
        byId.get(parentId).replies.push(comment);
      } else {
        roots.push(comment);
      }
    });

    roots.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    roots.forEach((root) => {
      root.replies.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    });

    return roots;
  }, [comments]);

  const getReplyState = (commentId, total) => {
    const entry = replyVisibility[commentId] || { expanded: false, visibleCount: DEFAULT_VISIBLE_REPLIES };
    const visibleCount = Math.min(entry.visibleCount || DEFAULT_VISIBLE_REPLIES, total);
    return { ...entry, visibleCount };
  };

  const toggleReplies = (commentId) => {
    setReplyVisibility((prev) => {
      const entry = prev[commentId] || { expanded: false, visibleCount: DEFAULT_VISIBLE_REPLIES };
      return {
        ...prev,
        [commentId]: {
          ...entry,
          expanded: !entry.expanded,
          visibleCount: entry.expanded ? DEFAULT_VISIBLE_REPLIES : entry.visibleCount,
        },
      };
    });
  };

  const showMoreReplies = (commentId, total) => {
    setReplyVisibility((prev) => {
      const entry = prev[commentId] || { expanded: true, visibleCount: DEFAULT_VISIBLE_REPLIES };
      return {
        ...prev,
        [commentId]: {
          ...entry,
          expanded: true,
          visibleCount: Math.min(total, (entry.visibleCount || DEFAULT_VISIBLE_REPLIES) + DEFAULT_VISIBLE_REPLIES),
        },
      };
    });
  };

  const loadCase = async () => {
    try {
      setLoading(true);
      const response = await communityAPI.getCase(id);
      setCaseData(response.data);
      const viewerRatings = response.data?.viewerRating?.ratings;
      if (viewerRatings) {
        setRatings({
          realism: viewerRatings.realism || 0,
          difficulty: viewerRatings.difficulty || 0,
          markSchemeQuality: viewerRatings.markSchemeQuality || 0,
          examinerQuestions: viewerRatings.examinerQuestions ?? null,
          learningMaterials: viewerRatings.learningMaterials ?? null
        });
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to load this community case.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      setCommentsLoading(true);
      const response = await communityAPI.listComments(id, { page: commentPage, limit: 10 });
      setComments(response.data?.comments || []);
      setCommentPagination(response.data?.pagination || { pages: 1, total: 0 });
    } catch (err) {
      console.error('Failed to load comments', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  useEffect(() => {
    loadCase();
  }, [id]);

  useEffect(() => {
    loadComments();
  }, [id, commentPage]);

  const loadImportSummary = useCallback(async () => {
    setImportSummaryLoading(true);
    try {
      const response = await api.get('/cases', {
        params: {
          library: 'personal',
          originType: 'community',
          limit: 500,
          sortBy: 'updatedAt',
          sortOrder: 'desc'
        }
      });
      const importedCases = response.data?.cases || [];
      const matches = importedCases.filter((caseItem) => resolveCommunityOriginId(caseItem) === id);
      if (!matches.length) {
        setImportSummary(null);
        return;
      }
      const summary = matches.reduce((acc, caseItem) => {
        const lastOriginVersion = Number(
          caseItem?.provenance?.lastOriginVersion ?? caseItem?.provenance?.sourceVersion ?? caseItem?.sourceVersion ?? 0
        );
        const importedAt = new Date(caseItem?.createdAt || caseItem?.updatedAt || 0).getTime() || 0;
        if (!acc.latestCopyId || importedAt > acc.latestImportedAt) {
          acc.latestCopyId = caseItem._id;
          acc.latestImportedAt = importedAt;
        }
        acc.latestOriginVersion = Math.max(acc.latestOriginVersion || 0, lastOriginVersion || 0);
        acc.count += 1;
        return acc;
      }, { count: 0, latestCopyId: '', latestOriginVersion: 0, latestImportedAt: 0 });

      setImportSummary(summary);
    } catch (err) {
      console.error('Failed to load import summary', err);
    } finally {
      setImportSummaryLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadImportSummary();
  }, [loadImportSummary]);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleImport = async () => {
    setActionState({ action: 'import' });
    try {
      await communityAPI.importCase(id);
      setSnackbar({ open: true, message: 'Case imported to your personal library.', severity: 'success' });
      loadImportSummary();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to import this case.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setActionState({ action: null });
    }
  };

  const handleFork = async () => {
    setActionState({ action: 'fork' });
    try {
      await communityAPI.forkCase(id);
      setSnackbar({ open: true, message: 'Case forked to your personal library.', severity: 'success' });
      loadImportSummary();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to fork this case.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setActionState({ action: null });
    }
  };

  const handleSubmitComment = async () => {
    if (!commentInput.trim()) {
      return;
    }
    if (commentInput.length > COMMENT_MAX_LENGTH) {
      setSnackbar({ open: true, message: 'Comment is too long. Please keep it under 5000 characters.', severity: 'warning' });
      return;
    }
    try {
      await communityAPI.createComment(id, { body: commentInput });
      setCommentInput('');
      setCommentPage(1);
      loadComments();
      setSnackbar({ open: true, message: 'Comment posted.', severity: 'success' });
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to add comment.';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const handleSubmitReply = async (parentId) => {
    if (!replyDraft.body.trim()) {
      return;
    }
    if (replyDraft.body.length > COMMENT_MAX_LENGTH) {
      setSnackbar({ open: true, message: 'Reply is too long. Please keep it under 5000 characters.', severity: 'warning' });
      return;
    }
    try {
      await communityAPI.createComment(id, { body: replyDraft.body, parentId });
      setReplyDraft({ parentId: null, body: '' });
      setCommentPage(1);
      loadComments();
      setSnackbar({ open: true, message: 'Reply posted.', severity: 'success' });
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to add reply.';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const renderCommentNode = (comment, depth = 0) => {
    const isReplying = replyDraft.parentId?.toString?.() === comment._id?.toString?.();
    const replies = Array.isArray(comment.replies) ? comment.replies : [];
    const replyState = getReplyState(comment._id, replies.length);
    const visibleReplies = replyState.expanded ? replies.slice(0, replyState.visibleCount) : [];
    return (
      <Paper
        key={comment._id}
        variant="outlined"
        sx={{
          p: 2,
          ml: depth ? 3 : 0,
          borderLeft: depth ? '2px solid rgba(22, 160, 133, 0.4)' : 'none'
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">{buildAuthorName(comment.userId)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeDate(comment.createdAt)}
            </Typography>
          </Stack>
          <Typography variant="body2">
            {comment.body}
          </Typography>
          <Button
            size="small"
            onClick={() => setReplyDraft({ parentId: comment._id, body: '' })}
          >
            Reply
          </Button>
          {replies.length > 0 && (
            <Button
              size="small"
              onClick={() => toggleReplies(comment._id)}
            >
              {replyState.expanded ? 'Hide replies' : `Show replies (${replies.length})`}
            </Button>
          )}
          {isReplying && (
            <Stack spacing={1} sx={{ pl: 2 }}>
              <TextField
                label="Write a reply"
                multiline
                minRows={2}
                value={replyDraft.body}
                onChange={(event) => setReplyDraft((prev) => ({ ...prev, body: event.target.value }))}
                error={replyTooLong}
                helperText={
                  replyTooLong
                    ? `Reply is too long (${replyDraft.body.length}/${COMMENT_MAX_LENGTH}).`
                    : `${replyDraft.body.length}/${COMMENT_MAX_LENGTH}`
                }
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => handleSubmitReply(comment._id)}
                  disabled={replyTooLong || !replyDraft.body.trim()}
                >
                  Post reply
                </Button>
                <Button
                  variant="text"
                  onClick={() => setReplyDraft({ parentId: null, body: '' })}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          )}
          {visibleReplies.length > 0 && (
            <Stack spacing={1}>
              {visibleReplies.map((reply) => renderCommentNode(reply, depth + 1))}
              {replyState.expanded && replyState.visibleCount < replies.length && (
                <Button size="small" onClick={() => showMoreReplies(comment._id, replies.length)}>
                  Show more replies
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>
    );
  };

  const handleSubmitRating = async () => {
    const requiredValues = [ratings.realism, ratings.markSchemeQuality, ratings.difficulty];
    if (requiredValues.some((value) => !value || value < 1)) {
      setSnackbar({
        open: true,
        message: 'Please rate patient narrative, mark scheme quality, and difficulty from 1 to 5.',
        severity: 'warning'
      });
      return;
    }

    setRatingSubmitting(true);
    try {
      const payloadRatings = {
        realism: ratings.realism,
        difficulty: ratings.difficulty,
        markSchemeQuality: ratings.markSchemeQuality
      };

      if (hasExaminerQuestions) {
        payloadRatings.examinerQuestions = Number.isFinite(ratings.examinerQuestions) && ratings.examinerQuestions > 0
          ? ratings.examinerQuestions
          : null;
      }

      if (hasLearningMaterials) {
        payloadRatings.learningMaterials = Number.isFinite(ratings.learningMaterials) && ratings.learningMaterials > 0
          ? ratings.learningMaterials
          : null;
      }

      await communityAPI.rateCase(id, { ratings: payloadRatings });
      setSnackbar({ open: true, message: 'Thanks for rating this case.', severity: 'success' });
      loadCase();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to submit rating.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handleReport = async () => {
    if (!reportDialog.reason.trim()) {
      setSnackbar({ open: true, message: 'Please enter a report reason.', severity: 'warning' });
      return;
    }

    try {
      await communityAPI.reportCase(id, {
        reason: reportDialog.reason.trim(),
        details: reportDialog.details.trim()
      });
      setReportDialog({ open: false, reason: '', details: '' });
      setSnackbar({ open: true, message: 'Report submitted. Thank you.', severity: 'success' });
      loadCase();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to submit report.';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 6 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!caseData) {
    return (
      <Container sx={{ py: 6 }}>
        <Alert severity="info">Case not found.</Alert>
      </Container>
    );
  }

  const allowComments = caseData.community?.allowComments !== false;
  const allowRatings = caseData.community?.allowRatings !== false;
  const hasImported = Boolean(importSummary?.count);
  const communityVersion = Number(caseData.version || 0);
  const hasUpdate = hasImported
    && communityVersion
    && Number(importSummary.latestOriginVersion || 0) < communityVersion;
  const importLabel = hasUpdate
    ? 'Import update'
    : (hasImported ? 'Import another copy' : 'Import to my library');
  const commentTooLong = commentInput.length > COMMENT_MAX_LENGTH;
  const replyTooLong = replyDraft.body.length > COMMENT_MAX_LENGTH;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/community-library')} sx={{ mb: 2 }}>
        Back to Community Library
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {caseData.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              By {buildAuthorName(caseData.author)}
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Box display="flex" alignItems="center" gap={0.5}>
              <StarRateIcon fontSize="small" color="warning" />
              <Typography variant="body2">
                {Number(stats.averageRating || 0).toFixed(1)} ({stats.ratingCount || 0})
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Typography variant="body2" color="text.secondary">
              Updated {formatRelativeDate(caseData.updatedAt || caseData.community?.publishedAt)}
            </Typography>
          </Stack>

          {caseData.shortDescription && (
            <Typography variant="body1" color="text.secondary">
              {caseData.shortDescription}
            </Typography>
          )}

          {Array.isArray(caseData.tags) && caseData.tags.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {caseData.tags.map((tag) => (
                <Chip key={`${caseData._id}-${tag}`} label={tag} size="small" />
              ))}
            </Stack>
          )}

          {badges.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {badges.map((badge) => (
                <Chip key={`${caseData._id}-${badge}`} label={badge} size="small" color="info" variant="outlined" />
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Case Overview</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Presenting complaint</Typography>
                <Typography variant="body2">{caseData.presentingComplaint}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Specialty</Typography>
                <Typography variant="body2">{caseData.specialty}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Difficulty</Typography>
                <Typography variant="body2">{caseData.difficulty}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Published</Typography>
                <Typography variant="body2">{formatDateTime(caseData.community?.publishedAt)}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Discussion</Typography>
            {allowComments ? (
              <>
                <Stack spacing={2} sx={{ mb: 2 }}>
                  {threadedComments.map((comment) => renderCommentNode(comment))}
                </Stack>

                {commentsLoading && <CircularProgress size={20} />}

                {commentPagination.pages > 1 && (
                  <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                    <Pagination
                      count={commentPagination.pages}
                      page={commentPage}
                      onChange={(_event, page) => setCommentPage(page)}
                    />
                  </Box>
                )}

                <Stack spacing={2}>
                  <TextField
                    label="Add a comment"
                    multiline
                    minRows={3}
                    value={commentInput}
                    onChange={(event) => setCommentInput(event.target.value)}
                    error={commentTooLong}
                    helperText={
                      commentTooLong
                        ? `Comment is too long (${commentInput.length}/${COMMENT_MAX_LENGTH}).`
                        : `${commentInput.length}/${COMMENT_MAX_LENGTH}`
                    }
                  />
                  <Box>
                    <Button
                      variant="contained"
                      onClick={handleSubmitComment}
                      disabled={commentTooLong || !commentInput.trim()}
                    >
                      Post Comment
                    </Button>
                  </Box>
                </Stack>
              </>
            ) : (
              <Alert severity="info">Comments are disabled for this case.</Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Actions</Typography>
              <Stack spacing={1}>
                {hasImported && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/cases/${importSummary.latestCopyId}`)}
                  >
                    Open latest copy
                  </Button>
                )}
                {isOriginalAuthor && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/cases/${caseData._id}/edit`)}
                  >
                    Edit case
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  disabled={actionState.action === 'import'}
                  onClick={handleImport}
                >
                  {importLabel}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CallSplitIcon />}
                  disabled={actionState.action === 'fork'}
                  onClick={handleFork}
                >
                  Fork and edit
                </Button>
                <Button
                  variant="text"
                  startIcon={<FlagIcon />}
                  onClick={() => setReportDialog((prev) => ({ ...prev, open: true }))}
                >
                  Report case
                </Button>
              </Stack>
              {importSummaryLoading && (
                <Typography variant="caption" color="text.secondary">
                  Checking your library...
                </Typography>
              )}
              {hasImported && (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    You already have this case in your library ({importSummary.count} {importSummary.count > 1 ? 'copies' : 'copy'}).
                  </Typography>
                  {hasUpdate && (
                    <Typography variant="caption" color="warning.main">
                      An updated community version is available.
                    </Typography>
                  )}
                </Stack>
              )}
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Rate this case</Typography>
              {allowRatings ? (
                <Stack spacing={2}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160 }}>
                        Patient narrative
                      </Typography>
                      <Rating
                        value={ratings.realism}
                        onChange={(_event, value) => setRatings((prev) => ({ ...prev, realism: value || 0 }))}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160 }}>
                        Mark scheme quality
                      </Typography>
                      <Rating
                        value={ratings.markSchemeQuality}
                        onChange={(_event, value) => setRatings((prev) => ({ ...prev, markSchemeQuality: value || 0 }))}
                      />
                    </Box>
                  </Box>
                  <Divider />
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160 }}>
                        Difficulty
                      </Typography>
                      <Rating
                        value={ratings.difficulty}
                        onChange={(_event, value) => setRatings((prev) => ({ ...prev, difficulty: value || 0 }))}
                      />
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Difficulty ratings are tracked separately and do not affect the average score.
                  </Typography>
                  {hasExaminerQuestions && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160 }}>
                          Examiner questions
                        </Typography>
                        <Rating
                          value={ratings.examinerQuestions ?? null}
                          onChange={(_event, value) => setRatings((prev) => ({ ...prev, examinerQuestions: value ?? null }))}
                        />
                      </Box>
                    </Box>
                  )}
                  {hasLearningMaterials && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160 }}>
                          Learning materials
                        </Typography>
                        <Rating
                          value={ratings.learningMaterials ?? null}
                          onChange={(_event, value) => setRatings((prev) => ({ ...prev, learningMaterials: value ?? null }))}
                        />
                      </Box>
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleSubmitRating}
                    disabled={ratingSubmitting}
                  >
                    Submit Rating
                  </Button>
                  {caseData.viewerRating?.updatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Your rating was last updated {formatRelativeDate(caseData.viewerRating.updatedAt)}.
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Alert severity="info">Ratings are disabled for this case.</Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1}>
                <Typography variant="subtitle2">Community averages</Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="span">
                    Patient narrative: {Number(stats.ratings?.realism || 0).toFixed(1)}
                  </Box>
                  <StarRateIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="span">
                    Mark scheme quality: {Number(stats.ratings?.markSchemeQuality || 0).toFixed(1)}
                  </Box>
                  <StarRateIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="span">
                    Difficulty: {Number(stats.ratings?.difficulty || 0).toFixed(1)}
                  </Box>
                  <StarRateIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                </Typography>
                {hasExaminerQuestions && (
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span">
                      Examiner questions: {Number(stats.ratings?.examinerQuestions || 0).toFixed(1)}
                    </Box>
                    <StarRateIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                  </Typography>
                )}
                {hasLearningMaterials && (
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span">
                      Learning materials: {Number(stats.ratings?.learningMaterials || 0).toFixed(1)}
                    </Box>
                    <StarRateIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Dialog
        open={reportDialog.open}
        onClose={() => setReportDialog({ open: false, reason: '', details: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Report case</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Reason"
              required
              value={reportDialog.reason}
              onChange={(event) => setReportDialog((prev) => ({ ...prev, reason: event.target.value }))}
            />
            <TextField
              label="Additional details"
              multiline
              minRows={3}
              value={reportDialog.details}
              onChange={(event) => setReportDialog((prev) => ({ ...prev, details: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialog({ open: false, reason: '', details: '' })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleReport}>
            Submit report
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CommunityCasePage;
